import { useState } from 'react';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Avatar, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FeatureSubscriptionType, useUser_GetUserByIdQuery } from 'queries';

import Spinner from 'common/spinner';
import PortalUsersAutocomplete, {
  UserAutoCompleteOption,
} from 'pages/common/portal/portal-users-autocomplete';
import { canAddPointPerson } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    fontSize: '12px',
    background: '#1C2541',
    color: '#F5F5F5',
    width: '24px',
    height: '24px',
    marginRight: '10px',
  },
  iconButton: {
    padding: '0px',
    color: '#1C2541',
  },
  editContainer: {
    padding: '10px 20px',
    display: 'flex',
    width: '100%',
  },
}));

type Props = {
  userId?: string;
  estateId: string;
  portalId: string;
  onChange: (estateId: string, selectedUserId: string | null) => void;
  featureSubscriptionType: FeatureSubscriptionType;
};

const EstatePointPerson = ({
  userId,
  estateId,
  portalId,
  onChange,
  featureSubscriptionType,
}: Props) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);

  const { data: userData, loading: loadingUserData } = useUser_GetUserByIdQuery({
    skip: !userId,
    variables: { id: userId || '' },
    onError: (e) => {
      showAlert({
        message: 'Error getting user by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onAddPointPersonClicked = () => {
    if (!canAddPointPerson(featureSubscriptionType)) {
      showToast({
        message: 'Please upgrade your account to add a point person to this estate.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
    setIsEditing(true);
  };

  const handleAssignedUserChange = (
    _elementId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _e: any,
    option: UserAutoCompleteOption | null,
  ) => {
    const selectedUserId = option?.id || null;
    onChange(estateId, selectedUserId);
    setIsEditing(false);
  };

  if (loadingUserData) {
    return <Spinner />;
  }

  if (isEditing) {
    return (
      <div className={classes.editContainer}>
        <PortalUsersAutocomplete
          elementId={'assignedUserId_' + estateId}
          portalId={portalId}
          selectedUserId={userId}
          handleUserChange={handleAssignedUserChange}
          handleLoseFocus={() => setIsEditing(false)}
          openByDefault={true}
        />
      </div>
    );
  }

  if (!userId || !userData?.data) {
    return (
      <IconButton className={classes.iconButton} onClick={onAddPointPersonClicked}>
        <PersonAddIcon />
      </IconButton>
    );
  }

  return (
    <div className={classes.container} onClick={onAddPointPersonClicked}>
      {userData.data.avatarUrl ? (
        <Avatar className={classes.avatar} src={userData.data.avatarUrl} />
      ) : (
        <Avatar className={classes.avatar}>{userData.data.firstName.charAt(0)}</Avatar>
      )}
      <Typography>
        {userData.data.firstName} {userData.data.lastName.charAt(0)}.
      </Typography>
    </div>
  );
};

export default EstatePointPerson;
